import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, SubmitHandler } from 'react-hook-form';

import { Form, FormTextArea } from 'components/form';
import { formatDateShort } from 'utils/formatters';

import * as styled from './styles';
import { Lightbox } from './ui/lightbox';

type Props = {
  thread: Kudable.MessageThreadData['thread'];
  form: UseFormReturn<MessageThread.ReplyFormValues>;
  onSendReply: SubmitHandler<MessageThread.ReplyFormValues>;
};

export const MessageThreadScene = ({ thread, form, onSendReply }: Props) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [thread.messages.length]);

  return (
    <styled.Page>
      <styled.TopBar>
        <styled.Logo>Kudable</styled.Logo>
      </styled.TopBar>

      <styled.Container>
        <styled.Card>
          <styled.Header>
            <styled.HeaderTitle>Conversation</styled.HeaderTitle>
            {thread.service_name && (
              <styled.ServiceName>{thread.service_name}</styled.ServiceName>
            )}
            <styled.ThreadDate>
              Started {formatDateShort(thread.inserted_at)}
            </styled.ThreadDate>
          </styled.Header>

          <styled.ThreadBody>
            <styled.Bubble $sender="client">
              <styled.BubbleSender>You</styled.BubbleSender>
              <styled.BubbleText>{thread.initial_message}</styled.BubbleText>
            </styled.Bubble>

            {thread.messages.map((m) => {
              const photos = m.asset_urls ?? [];
              return (
                <styled.Bubble
                  key={m.id}
                  $sender={m.sender === 'client' ? 'client' : 'provider'}
                >
                  <styled.BubbleSender>
                    {m.sender === 'client' ? 'You' : 'Provider'}
                  </styled.BubbleSender>
                  <styled.BubbleText>{m.body}</styled.BubbleText>
                  {photos.length > 0 ? (
                    <styled.PhotosRow>
                      {photos.map((url, i) => (
                        <styled.PhotoButton
                          key={i}
                          type="button"
                          onClick={() => setLightboxUrl(url)}
                        >
                          <styled.PhotoTile src={url} alt="" />
                        </styled.PhotoButton>
                      ))}
                    </styled.PhotosRow>
                  ) : null}
                  <styled.BubbleTime>
                    {formatDateShort(m.inserted_at)}
                  </styled.BubbleTime>
                </styled.Bubble>
              );
            })}

            {thread.status === 'new' && thread.messages.length === 0 && (
              <styled.Waiting>Waiting for a response…</styled.Waiting>
            )}

            <div ref={bottomRef} />
          </styled.ThreadBody>

          <Form form={form} onSubmit={onSendReply}>
            <styled.ReplyBox>
              <FormTextArea
                name="reply"
                placeholder="Write a reply…"
                rows={3}
              />
              <styled.SendBtn
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Sending…' : 'Send'}
              </styled.SendBtn>
            </styled.ReplyBox>
          </Form>
        </styled.Card>
      </styled.Container>

      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </styled.Page>
  );
};
